import { useEffect, useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";
import { api } from "../lib/api";
import { useCapabilities } from "../lib/access";
import { lastDashboard } from "../lib/lastDashboard";

/** Land on the dashboard they last opened, else their first, creating a first one if needed. */
export function Home() {
  const navigate = useNavigate();
  const can = useCapabilities();
  const me = useQuery({ queryKey: ["me"], queryFn: api.me, retry: false, staleTime: Infinity });
  const qc = useQueryClient();
  const dashboards = useQuery({ queryKey: ["dashboards"], queryFn: api.dashboards });
  // Effects can run twice (StrictMode) before the mutation reports pending; only ever create one.
  const creating = useRef(false);
  const create = useMutation({
    mutationFn: () => api.createDashboard("My dashboard"),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ["dashboards"] });
      navigate({ to: "/d/$id", params: { id: d.dashboard.id }, replace: true });
    },
  });
  useEffect(() => {
    if (!dashboards.data || !me.data) return;
    // Not while viewing as someone: theirs is never remembered, so this would be staff's own.
    const last = can.viewAs ? null : lastDashboard(me.data.userId);
    const first =
      dashboards.data.find((d) => d.id === last && (!d.sharedBy || d.shareStatus === "accepted")) ??
      dashboards.data.find((d) => !d.sharedBy) ??
      dashboards.data.find((d) => d.shareStatus === "accepted");
    if (first) navigate({ to: "/d/$id", params: { id: first.id }, replace: true });
    // Only shares they haven't added yet: show them those rather than making an empty dashboard.
    else if (dashboards.data.length) navigate({ to: "/shared", replace: true });
    // Never on someone's behalf: staff viewing as them add one from the sidebar if they mean to.
    else if (!can.viewAs && !creating.current) {
      creating.current = true;
      create.mutate();
    }
  }, [dashboards.data, me.data, navigate, create, can.viewAs]);
  if (dashboards.data?.length === 0 && can.viewAs) return <p className="p-8 text-sm text-muted">They don't have any dashboards yet.</p>;
  return null;
}
